import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import './maintenance/Maintenance.css';
import DemandesListe from './maintenance/DemandesListe';
import DemandeForm from './maintenance/DemandeForm';
import DemandeDetail from './maintenance/DemandeDetail';
import MaintenanceDashboard from './maintenance/MaintenanceDashboard';
import MaintenancePlanning from './maintenance/MaintenancePlanning';
import MaintenanceConfig from './maintenance/MaintenanceConfig';

export default function Maintenance({ currentUser, setHeaderAction }) {
  const isAdmin = currentUser.role === 'admin' || currentUser.role === 'super-admin';

  const [tab, setTab] = useState('liste');        // liste | planning | dashboard | config
  const [nav, setNav] = useState({ view: 'list' }); // list | { view:'form', id? } | { view:'detail', id }
  const [preset, setPreset] = useState(null);      // filtre injecté depuis le tableau de bord / planning
  const [refs, setRefs] = useState({ sites: [], activites: [], prestataires: [], users: [] });

  const openDetail = useCallback((id) => setNav({ view: 'detail', id }), []);
  const openForm = useCallback((id) => setNav({ view: 'form', id: id || null }), []);
  const backToList = useCallback(() => setNav({ view: 'list' }), []);
  const fromDashboard = useCallback((filter) => { setPreset({ ...filter, _ts: Date.now() }); setTab('liste'); }, []);

  const loadRefs = useCallback(async () => {
    try {
      const reqs = [
        api.get('/sites'),
        api.get('/maintenance-activites', { params: { actif: true } }),
        api.get('/maintenance-prestataires', { params: { actif: true } }),
      ];
      if (isAdmin) reqs.push(api.get('/users'));
      const [sites, activites, prestataires, users] = await Promise.all(reqs);
      setRefs({
        sites: sites.data,
        activites: activites.data,
        prestataires: prestataires.data,
        users: users ? users.data : [],
      });
    } catch {
      /* selects vides si erreur — non bloquant */
    }
  }, [isAdmin]);
  useEffect(() => { loadRefs(); }, [loadRefs]);

  // Bouton d'action en haut à droite (façon CRM atelier) : seulement sur la liste.
  useEffect(() => {
    if (!setHeaderAction) return undefined;
    if (tab === 'liste' && nav.view === 'list') {
      setHeaderAction(<button className="page-action-btn" onClick={() => openForm(null)}>+ Nouvelle demande</button>);
    } else {
      setHeaderAction(null);
    }
    return () => setHeaderAction(null);
  }, [tab, nav.view, openForm, setHeaderAction]);

  // Écrans pleins (hors onglets) : création/édition + détail
  if (nav.view === 'form') {
    return (
      <div className="page-container">
        <DemandeForm currentUser={currentUser} refs={refs} demandeId={nav.id || null} onCancel={backToList} onSaved={(id) => openDetail(id)} />
      </div>
    );
  }
  if (nav.view === 'detail') {
    return (
      <div className="page-container">
        <DemandeDetail currentUser={currentUser} refs={refs} demandeId={nav.id} onBack={backToList} onEdit={(id) => openForm(id)} />
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-tabs">
        <button className={tab === 'liste' ? 'active' : ''} onClick={() => setTab('liste')}>📋 Demandes</button>
        <button className={tab === 'planning' ? 'active' : ''} onClick={() => setTab('planning')}>📅 Planning</button>
        <button className={tab === 'dashboard' ? 'active' : ''} onClick={() => setTab('dashboard')}>📊 Tableau de bord</button>
        {isAdmin && <button className={tab === 'config' ? 'active' : ''} onClick={() => setTab('config')}>⚙️ Configuration</button>}
      </div>

      {tab === 'liste' && (
        <DemandesListe currentUser={currentUser} refs={refs} preset={preset} onNew={() => openForm(null)} onOpen={openDetail} onEdit={openForm} />
      )}
      {tab === 'planning' && (
        <MaintenancePlanning currentUser={currentUser} onOpen={openDetail} />
      )}
      {tab === 'dashboard' && (
        <MaintenanceDashboard currentUser={currentUser} onCounterClick={fromDashboard} />
      )}
      {tab === 'config' && isAdmin && (
        <MaintenanceConfig currentUser={currentUser} onChanged={loadRefs} />
      )}
    </div>
  );
}
